// Transports, déplacements et vacances.
import type { EventDef } from '../events';
import type { GameState } from '../../engine/state';
import { carNeedsInspection, carUnderWarranty } from '../../game/car';
import { age, between, simpleEvent, working } from './kit';

/** Pendant les études, les billets et les dépenses sont plus modestes. */
const student = (state: GameState): boolean => state.stage === 'prologue';

/** La carte de réduction jeune (événement carte_reduction_jeune) fait baisser les billets de train jusqu'à 27 ans. */
const youthCard = (state: GameState): boolean => state.flags.carte_reduction_jeune === true && age(state) <= 27;

/** Un abonnement de bus ou de tram : pas de voiture, et une dépense de transport. */
const busPass = (state: GameState): boolean => !state.hasCar && (state.expenses.transport?.amount ?? 0) >= 20;

/** Une voiture neuve, dans ses deux premières années. */
const newCar = (state: GameState): boolean => state.hasCar && state.carNew;

/** Une voiture d'occasion, ou une neuve qui a passé deux ans. */
const usedCar = (state: GameState): boolean => state.hasCar && !state.carNew;

/** Un vélo loué au mois après un vol ; le vélo retrouvé permet d'arrêter la location. */
const BIKE_RENTAL = 15;

export const EVENTS: EventDef[] = [
  // La voiture : seulement pour qui en a une.
  simpleEvent({
    id: 'panne_voiture',
    kind: 'imprevu',
    stages: ['stage1', 'stage2'],
    bad: true,
    shock: 8,
    condition: usedCar,
    params: (state) => {
      const cost = between(state, 700, 1200);
      // Une pièce de réemploi (d'occasion) coûte environ 40 % de moins.
      return { cost, reuse: Math.round((cost * 0.6) / 10) * 10 };
    },
    page: 'fonds_urgence',
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost }) },
      { id: 'credit', effects: (_, p) => ({ loan: { amount: p.cost, rate: 0.15, months: 12 } }) },
      { id: 'reemploi', effects: (_, p) => ({ cash: -p.reuse, energy: -3 }) },
      { id: 'garantie', visible: carUnderWarranty, effects: { energy: -2 } },
    ],
  }),
  simpleEvent({
    id: 'marche_arriere',
    kind: 'imprevu',
    stages: ['stage1', 'stage2'],
    bad: true,
    shock: 6,
    condition: (state) => state.hasCar,
    // Déclarer un accident dont on est responsable fait grimper l'assurance d'environ 25 %.
    params: (state) => ({
      repair: between(state, 450, 700),
      franchise: between(state, 200, 300),
      malus: Math.max(5, Math.round((state.expenses.assuranceAuto?.amount ?? 90) * 0.25)),
    }),
    choices: [
      {
        id: 'declarer',
        effects: (_, p) => ({ cash: -p.franchise, addExpense: { id: 'assuranceAuto', amount: p.malus } }),
      },
      { id: 'livret', effects: (_, p) => ({ livret: -p.repair }) },
      { id: 'payer', effects: (_, p) => ({ cash: -p.repair }) },
    ],
  }),
  simpleEvent({
    id: 'controle_technique',
    kind: 'imprevu',
    stages: ['stage1', 'stage2'],
    bad: true,
    shock: 6,
    // Un contrôle tous les deux ans, le premier quatre ans après la mise en circulation.
    cooldownDays: 730,
    condition: carNeedsInspection,
    params: (state) => {
      const cost = between(state, 280, 420);
      return { cost, low: Math.round((cost * 0.7) / 5) * 5 };
    },
    page: 'fonds_urgence',
    choices: [
      { id: 'garage', effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost }) },
      { id: 'centre', effects: (_, p) => ({ cash: -p.low, energy: -3 }) },
    ],
  }),
  // La voiture neuve, pendant ses deux premières années : sous garantie, les pannes ne coûtent que du temps.
  simpleEvent({
    id: 'rappel_constructeur',
    kind: 'quotidien',
    stages: ['stage1', 'stage2'],
    maxCount: 1,
    condition: newCar,
    choices: [{ id: 'concession', effects: { energy: -2 } }, { id: 'attendre' }],
  }),
  simpleEvent({
    id: 'rayure_portiere',
    kind: 'imprevu',
    stages: ['stage1', 'stage2'],
    bad: true,
    shock: 5,
    maxCount: 1,
    condition: newCar,
    params: (state) => ({ cost: between(state, 250, 400), pen: between(state, 20, 30) }),
    choices: [
      { id: 'carrossier', effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost }) },
      { id: 'stylo', effects: (_, p) => ({ cash: -p.pen, energy: -1, wellbeing: -1 }) },
    ],
  }),
  simpleEvent({
    // Un garage indépendant qui suit le carnet d'entretien du constructeur garde la garantie (règle européenne).
    id: 'revision_concession',
    kind: 'quotidien',
    pleasureCategory: 'achats',
    stages: ['stage1', 'stage2'],
    maxCount: 2,
    condition: (state) => newCar(state) && carUnderWarranty(state),
    params: (state) => {
      const base = between(state, 180, 250);
      return { base, pack: between(state, 120, 180), garage: Math.round((base * 0.7) / 5) * 5 };
    },
    choices: [
      { id: 'concession', effects: (_, p) => ({ cash: -p.base }) },
      { id: 'pack', effects: (_, p) => ({ cash: -(p.base + p.pack), pleasure: 1 }) },
      { id: 'garage', effects: (_, p) => ({ cash: -p.garage, energy: -1, knowledge: 1 }) },
      { id: 'sauter', effects: { carWarranty: 'end' } },
    ],
  }),
  simpleEvent({
    id: 'extension_garantie_auto',
    kind: 'tentation',
    stages: ['stage1', 'stage2'],
    maxCount: 1,
    // Dans les six derniers mois de la garantie du neuf.
    condition: (state) => newCar(state) && carUnderWarranty(state) && state.carWarrantyUntil - state.day <= 182,
    params: (state) => ({ price: between(state, 400, 600) }),
    choices: [
      { id: 'prendre', effects: (_, p) => ({ cash: -p.price, carWarranty: 2 }) },
      { id: 'livret', effects: (_, p) => ({ livret: -p.price, carWarranty: 2 }) },
      { id: 'refuser' },
    ],
  }),
  simpleEvent({
    id: 'panne_garantie',
    kind: 'imprevu',
    stages: ['stage1', 'stage2'],
    bad: true,
    shock: 5,
    maxCount: 2,
    condition: (state) => newCar(state) && carUnderWarranty(state),
    params: (state) => ({ fare: between(state, 10, 20) }),
    choices: [
      { id: 'pret', effects: { energy: -1 } },
      { id: 'transports', effects: (_, p) => ({ cash: -p.fare, energy: -3 }) },
    ],
  }),

  // La voiture d'occasion (ou la neuve qui a passé deux ans) : l'usure, et les réparations à sa charge.
  simpleEvent({
    id: 'courroie_distribution',
    kind: 'quotidien',
    stages: ['stage1', 'stage2'],
    maxCount: 1,
    condition: usedCar,
    params: (state) => ({ cost: between(state, 450, 700) }),
    choices: [
      { id: 'changer', effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost }) },
      { id: 'attendre', effects: { followUp: { event: 'courroie_cassee', days: 150 } } },
    ],
  }),
  simpleEvent({
    // Cinq mois après avoir repoussé le changement de la courroie.
    id: 'courroie_cassee',
    kind: 'imprevu',
    stages: ['stage1', 'stage2'],
    bad: true,
    shock: 8,
    manual: true,
    condition: usedCar,
    params: (state) => ({ repair: between(state, 1800, 2600) }),
    page: 'fonds_urgence',
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: -p.repair }) },
      { id: 'credit', effects: (_, p) => ({ loan: { amount: p.repair, rate: 0.15, months: 24 } }) },
      { id: 'payer', effects: (_, p) => ({ cash: -p.repair }) },
    ],
  }),
  simpleEvent({
    id: 'batterie_hiver',
    kind: 'imprevu',
    stages: ['stage1', 'stage2'],
    bad: true,
    shock: 5,
    season: [12, 1, 2],
    condition: usedCar,
    params: (state) => {
      const battery = between(state, 110, 160);
      return { battery, total: battery + between(state, 60, 90) };
    },
    choices: [
      { id: 'depanneur', effects: (_, p) => ({ cash: -p.total }) },
      { id: 'voisin', effects: (_, p) => ({ cash: -p.battery, energy: -2 }) },
    ],
  }),
  simpleEvent({
    id: 'pneus_uses',
    kind: 'quotidien',
    stages: ['stage1', 'stage2'],
    maxCount: 2,
    cooldownDays: 730,
    condition: usedCar,
    params: (state) => {
      const cost = between(state, 180, 260);
      return { cost, low: Math.round((cost * 0.5) / 5) * 5 };
    },
    choices: [
      { id: 'neufs', effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'occasion', effects: (_, p) => ({ cash: -p.low, energy: -1 }) },
      { id: 'attendre', effects: { wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    id: 'voyant_moteur',
    kind: 'imprevu',
    stages: ['stage1', 'stage2'],
    bad: true,
    shock: 6,
    condition: usedCar,
    params: (state) => {
      const cost = between(state, 300, 450);
      return { cost, diy: Math.round((cost * 0.4) / 5) * 5 + 25 };
    },
    choices: [
      { id: 'garage', effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost }) },
      { id: 'lecteur', effects: (_, p) => ({ cash: -p.diy, energy: -4, knowledge: 1 }) },
      { id: 'garantie', visible: carUnderWarranty, effects: { energy: -1 } },
    ],
  }),
  simpleEvent({
    id: 'flash_radar',
    kind: 'imprevu',
    stages: ['stage1', 'stage2'],
    bad: true,
    shock: 5,
    maxCount: 3,
    condition: (state) => state.hasCar,
    // Moins de 20 km/h au-dessus de 50 : 135 €, ramenés à 90 € si l'on paie dans les quinze jours.
    params: (state) => ({ forfait: 135, minoree: 90, economie: between(state, 5, 10) }),
    choices: [
      { id: 'payer', effects: (_, p) => ({ cash: -p.minoree }) },
      {
        id: 'douceur',
        effects: (_, p) => ({
          cash: -p.minoree,
          wellbeing: -1,
          addExpense: { id: 'carburant', amount: -p.economie },
        }),
      },
    ],
  }),
  simpleEvent({
    id: 'amende_stationnement',
    kind: 'quotidien',
    stages: ['stage1', 'stage2'],
    condition: (state) => state.hasCar,
    params: () => ({ fine: 35 }),
    choices: [
      { id: 'contester', effects: { energy: -3, wellbeing: -1 } },
      { id: 'payer', effects: (_, p) => ({ cash: -p.fine }) },
    ],
  }),
  simpleEvent({
    id: 'hausse_carburant',
    kind: 'quotidien',
    stages: ['stage1', 'stage2'],
    condition: (state) => state.hasCar,
    // Le litre prend 12 % : le budget carburant suit.
    params: (state) => ({ hausse: Math.max(5, Math.round((state.expenses.carburant?.amount ?? 80) * 0.12)) }),
    page: 'inflation',
    choices: [
      { id: 'accepter', effects: (_, p) => ({ addExpense: { id: 'carburant', amount: p.hausse } }) },
      { id: 'covoiturer', effects: { wellbeing: -1 } },
      { id: 'velo', visible: (state) => state.hasBike, effects: { energy: -3 } },
    ],
  }),
  simpleEvent({
    id: 'assurance_auto_echeance',
    kind: 'quotidien',
    stages: ['stage1', 'stage2'],
    condition: (state) => state.hasCar,
    params: (state) => ({ hausse: between(state, 5, 10), tiers: between(state, 12, 20) }),
    choices: [
      { id: 'accepter', effects: (_, p) => ({ addExpense: { id: 'assuranceAuto', amount: p.hausse } }) },
      {
        // La hausse s'applique en attendant les devis, qui arrivent une semaine plus tard.
        id: 'comparer',
        effects: (_, p) => ({
          addExpense: { id: 'assuranceAuto', amount: p.hausse },
          energy: -2,
          followUp: { event: 'assurance_auto_devis', days: 7 },
        }),
      },
      { id: 'tiers', effects: (_, p) => ({ addExpense: { id: 'assuranceAuto', amount: -p.tiers }, wellbeing: -1 }) },
    ],
  }),
  simpleEvent({
    // Une semaine après avoir demandé des devis d'assurance auto.
    id: 'assurance_auto_devis',
    kind: 'quotidien',
    stages: ['stage1', 'stage2'],
    manual: true,
    params: (state) => ({ baisse: between(state, 8, 15) }),
    choices: [
      { id: 'changer', effects: (_, p) => ({ addExpense: { id: 'assuranceAuto', amount: -p.baisse }, energy: -1 }) },
      { id: 'rester' },
    ],
  }),
  simpleEvent({
    id: 'covoiturage_conducteur',
    kind: 'quotidien',
    stages: ['stage1', 'stage2'],
    condition: (state) => state.hasCar,
    params: (state) => ({ gain: between(state, 45, 75) }),
    choices: [
      { id: 'passagers', effects: (_, p) => ({ cash: p.gain, energy: -2 }) },
      { id: 'seul', effects: { wellbeing: 1 } },
    ],
  }),
  simpleEvent({
    id: 'batterie_a_plat',
    kind: 'quotidien',
    stages: ['stage1', 'stage2'],
    season: [12, 1, 2],
    cooldownDays: 300,
    condition: (state) => state.hasCar,
    params: (state) => ({ cost: between(state, 110, 170) }),
    choices: [
      { id: 'neuve', effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'cables', effects: { energy: -2, wellbeing: -1 } },
    ],
  }),

  // Le permis (un achat de la boutique) et la voiture de location.
  simpleEvent({
    // Le prêt à taux zéro des moins de 26 ans pour financer le permis : il ne donne pas le permis,
    // il avance l'argent pour l'acheter dans la boutique.
    id: 'permis_taux_zero',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    condition: (state) => !state.upgrades.includes('permis') && age(state) <= 25,
    params: () => ({ amount: 600, monthly: 30 }),
    choices: [
      { id: 'emprunter', effects: (_, p) => ({ cash: p.amount, loan: { amount: p.amount, rate: 0, months: 20 } }) },
      { id: 'non' },
    ],
  }),
  simpleEvent({
    id: 'location_voiture_weekend',
    kind: 'quotidien',
    pleasureCategory: 'voyages',
    stages: ['prologue', 'stage1', 'stage2'],
    condition: (state) => state.upgrades.includes('permis') && !state.hasCar && age(state) >= 21,
    params: (state) => ({ price: between(state, 140, 220), covoit: between(state, 25, 45) }),
    choices: [
      { id: 'louer', effects: (_, p) => ({ cash: -p.price, pleasure: 4 }) },
      { id: 'covoiturage', effects: (_, p) => ({ cash: -p.covoit, energy: -2, pleasure: 2 }) },
      { id: 'annuler' },
    ],
  }),

  // Le vélo.
  simpleEvent({
    id: 'velo_vole',
    kind: 'imprevu',
    stages: ['prologue', 'stage1', 'stage2'],
    bad: true,
    shock: 7,
    maxCount: 2,
    condition: (state) => state.hasBike,
    params: (state) => ({
      neuf: student(state) ? between(state, 250, 350) : between(state, 350, 500),
      occasion: between(state, 80, 130),
      location: BIKE_RENTAL,
    }),
    page: 'fonds_urgence',
    choices: [
      { id: 'neuf', effects: (_, p) => ({ livret: -p.neuf }) },
      { id: 'occasion', effects: (_, p) => ({ cash: -p.occasion, energy: -2 }) },
      {
        id: 'location',
        effects: (_, p) => ({ addExpense: { id: 'transport', amount: p.location }, flag: 'velo_vole_location' }),
      },
    ],
  }),
  simpleEvent({
    // Parfois, entre un mois et huit mois après le vol, le vélo est retrouvé.
    id: 'velo_retrouve',
    kind: 'chance',
    stages: ['prologue', 'stage1', 'stage2'],
    weight: 3,
    maxCount: 1,
    condition: (state) => {
      if (!state.hasBike || (state.eventCounts.velo_vole ?? 0) === 0) return false;
      const since = state.day - (state.eventLastDays.velo_vole ?? state.day);
      return since >= 30 && since <= 240;
    },
    params: (state) => ({ gain: between(state, 50, 100), location: BIKE_RENTAL }),
    choices: [
      { id: 'vendre', effects: (_, p) => ({ cash: p.gain }) },
      {
        id: 'reprendre',
        visible: (state) => state.flags.velo_vole_location === true,
        effects: (_, p) => ({ addExpense: { id: 'transport', amount: -p.location }, wellbeing: 1 }),
      },
      { id: 'donner', effects: { wellbeing: 2 } },
    ],
  }),
  simpleEvent({
    id: 'revision_velo',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    condition: (state) => state.hasBike,
    params: (state) => ({ adhesion: between(state, 10, 20), revision: between(state, 55, 85) }),
    choices: [
      {
        id: 'atelier',
        effects: (_, p) => ({ cash: -p.adhesion, energy: -3, xp: { skill: 'technique', amount: 8 } }),
      },
      { id: 'velociste', effects: (_, p) => ({ cash: -p.revision }) },
      { id: 'rustine', effects: { energy: -1, wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    id: 'velo_libre_service',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    condition: (state) => !state.hasBike && busPass(state),
    params: (state) => ({ price: between(state, 30, 45), saving: between(state, 8, 12) }),
    choices: [
      {
        id: 'abonner',
        effects: (_, p) => ({
          cash: -p.price,
          wellbeing: 1,
          addExpense: { id: 'transport', amount: -p.saving },
        }),
      },
      { id: 'bus' },
    ],
  }),

  // Bus, tram et métro.
  simpleEvent({
    id: 'greve_transports',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    condition: (state) => !state.hasCar,
    params: (state) => ({ cost: student(state) ? between(state, 10, 18) : between(state, 15, 30) }),
    choices: [
      { id: 'vtc', effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'velo', visible: (state) => state.hasBike, effects: { energy: -2 } },
      { id: 'marcher', visible: (state) => !state.hasBike, effects: { energy: -4 } },
      { id: 'maison', effects: { wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    id: 'controle_carte_oubliee',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    condition: busPass,
    params: () => ({ fine: 50 }),
    choices: [
      { id: 'agence', effects: { energy: -3, wellbeing: -1 } },
      { id: 'payer', effects: (_, p) => ({ cash: -p.fine }) },
    ],
  }),
  simpleEvent({
    id: 'dernier_metro',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    params: (state) => ({ taxi: student(state) ? between(state, 20, 35) : between(state, 25, 45) }),
    choices: [
      { id: 'taxi', effects: (_, p) => ({ cash: -p.taxi }) },
      { id: 'bus_nuit', effects: { energy: -3 } },
      { id: 'canape', effects: { energy: -1, wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    // L'employeur rembourse la moitié de l'abonnement de transport en commun. Qui l'a déjà appris
    // pendant son alternance (abonnement_transport_moitie, paquet « etudes ») ne le redécouvre pas.
    id: 'remboursement_abonnement',
    kind: 'chance',
    stages: ['stage1', 'stage2'],
    maxCount: 1,
    condition: (state) =>
      working(state) &&
      state.day - (state.firstJobDay ?? state.day) >= 60 &&
      !state.hasCar &&
      (state.expenses.transport?.amount ?? 0) >= 40 &&
      (state.eventCounts.abonnement_transport_moitie ?? 0) === 0,
    params: (state) => {
      const half = Math.round((state.expenses.transport?.amount ?? 60) / 2);
      return { half, gain: 3 * half };
    },
    choices: [
      {
        id: 'livret',
        effects: (_, p) => ({ livret: p.gain, wellbeing: 1, addExpense: { id: 'transport', amount: -p.half } }),
      },
      { id: 'compte', effects: (_, p) => ({ cash: p.gain, addExpense: { id: 'transport', amount: -p.half } }) },
    ],
  }),

  // Le train.
  simpleEvent({
    id: 'train_manque',
    kind: 'imprevu',
    stages: ['prologue', 'stage1', 'stage2'],
    bad: true,
    shock: 5,
    params: (state) => {
      const cost = student(state) ? between(state, 45, 80) : between(state, 70, 120);
      return { cost, reduced: Math.round(cost * 0.7), bus: between(state, 15, 30) };
    },
    choices: [
      { id: 'billet', visible: (state) => !youthCard(state), effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'carte', visible: youthCard, effects: (_, p) => ({ cash: -p.reduced }) },
      { id: 'bus', effects: (_, p) => ({ cash: -p.bus, energy: -4 }) },
    ],
  }),
  simpleEvent({
    id: 'retard_train_rembourse',
    kind: 'chance',
    pleasureCategory: 'repas',
    stages: ['prologue', 'stage1', 'stage2'],
    params: (state) => ({ gain: student(state) ? between(state, 20, 35) : between(state, 25, 55) }),
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.gain, wellbeing: 1 }) },
      { id: 'compte', effects: (_, p) => ({ cash: p.gain }) },
      { id: 'diner', effects: { pleasure: 2 } },
    ],
  }),
  simpleEvent({
    id: 'surclassement_train',
    kind: 'tentation',
    pleasureCategory: 'voyages',
    stages: ['prologue', 'stage1', 'stage2'],
    params: (state) => ({ price: student(state) ? between(state, 12, 20) : between(state, 20, 35) }),
    choices: [
      { id: 'premiere', effects: (_, p) => ({ cash: -p.price, pleasure: 2 }) },
      { id: 'rester' },
    ],
  }),
  simpleEvent({
    // La carte sert ensuite dans les autres événements de train, jusqu'à 27 ans.
    id: 'carte_reduction_jeune',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    season: [9],
    maxCount: 1,
    condition: (state) => age(state) <= 25,
    params: () => ({ price: 49 }),
    choices: [
      { id: 'prendre', effects: (_, p) => ({ cash: -p.price, flag: 'carte_reduction_jeune' }) },
      { id: 'non' },
    ],
  }),
  simpleEvent({
    id: 'billet_train_fetes',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    season: [11],
    cooldownDays: 300,
    condition: (state) => state.housing !== 'parents',
    // Une semaine plus tard, le billet coûte deux fois plus cher.
    params: (state) => {
      const early = student(state) ? between(state, 35, 60) : between(state, 45, 80);
      const late = 2 * early;
      return { early, reduced: Math.round(early * 0.7), late, lateReduced: Math.round(late * 0.7) };
    },
    choices: [
      {
        id: 'reserver',
        visible: (state) => !youthCard(state),
        effects: (_, p) => ({ cash: -p.early, wellbeing: 2 }),
      },
      { id: 'carte', visible: youthCard, effects: (_, p) => ({ cash: -p.reduced, wellbeing: 2 }) },
      { id: 'attendre', effects: { followUp: { event: 'billet_train_fetes_tard', days: 7 } } },
      { id: 'rester', effects: { wellbeing: -2 } },
    ],
  }),
  simpleEvent({
    // Une semaine après avoir attendu pour réserver le train des fêtes.
    id: 'billet_train_fetes_tard',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    manual: true,
    params: (state) => ({ covoit: between(state, 20, 35) }),
    choices: [
      {
        id: 'acheter',
        visible: (state) => !youthCard(state),
        effects: (_, p) => ({ cash: -p.late, wellbeing: 2 }),
      },
      { id: 'carte', visible: youthCard, effects: (_, p) => ({ cash: -p.lateReduced, wellbeing: 2 }) },
      { id: 'covoiturage', effects: (_, p) => ({ cash: -p.covoit, energy: -3, wellbeing: 2 }) },
      { id: 'rester', effects: { wellbeing: -2 } },
    ],
  }),

  // L'avion.
  simpleEvent({
    id: 'bagage_en_soute',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    params: (state) => ({ cabine: between(state, 15, 30), soute: between(state, 35, 55) }),
    choices: [
      { id: 'soute', effects: (_, p) => ({ cash: -p.soute, wellbeing: 1 }) },
      { id: 'cabine', effects: (_, p) => ({ cash: -p.cabine }) },
      { id: 'sac', effects: { wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    id: 'valise_perdue',
    kind: 'imprevu',
    stages: ['prologue', 'stage1', 'stage2'],
    bad: true,
    shock: 6,
    params: (state) => ({
      cost: student(state) ? between(state, 60, 100) : between(state, 90, 150),
      low: between(state, 20, 35),
    }),
    choices: [
      { id: 'racheter', effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'minimum', effects: (_, p) => ({ cash: -p.low, energy: -1, wellbeing: -2 }) },
    ],
  }),
  simpleEvent({
    id: 'vol_surreserve',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    season: [7, 8],
    cooldownDays: 300,
    params: (state) => ({ gain: between(state, 150, 300) }),
    choices: [
      { id: 'volontaire', effects: (_, p) => ({ cash: p.gain, energy: -3, wellbeing: -1 }) },
      { id: 'garder' },
    ],
  }),
  simpleEvent({
    // L'indemnité européenne d'un vol de moins de 1 500 km arrivé avec plus de trois heures de retard.
    id: 'indemnite_vol_retard',
    kind: 'chance',
    pleasureCategory: 'voyages',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 2,
    params: () => ({ gain: 250 }),
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.gain, wellbeing: 1 }) },
      { id: 'compte', effects: (_, p) => ({ cash: p.gain }) },
      { id: 'weekend', effects: { pleasure: 4 } },
    ],
  }),
  simpleEvent({
    id: 'passeport_voyage',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    params: () => ({ price: 86 }),
    choices: [
      { id: 'passeport', effects: (_, p) => ({ cash: -p.price, energy: -2 }) },
      { id: 'europe', effects: { wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    id: 'hors_forfait_etranger',
    kind: 'imprevu',
    stages: ['prologue', 'stage1', 'stage2'],
    bad: true,
    shock: 6,
    maxCount: 2,
    params: (state) => {
      const cost = student(state) ? between(state, 60, 110) : between(state, 80, 150);
      return { cost, low: Math.round(cost / 2) };
    },
    choices: [
      { id: 'payer', effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost }) },
      { id: 'negocier', effects: (_, p) => ({ cash: -p.low, energy: -3 }) },
    ],
  }),

  // Les vacances.
  simpleEvent({
    // Un programme européen offre des pass de train à des jeunes de 18 ans tirés au sort.
    id: 'pass_train_europe',
    kind: 'chance',
    pleasureCategory: 'voyages',
    stages: ['prologue'],
    weight: 3,
    maxCount: 1,
    condition: (state) => age(state) <= 18,
    params: (state) => ({ long: between(state, 400, 550), short: between(state, 150, 220) }),
    choices: [
      { id: 'long', effects: (_, p) => ({ cash: -p.long, pleasure: 6 }) },
      { id: 'court', effects: (_, p) => ({ cash: -p.short, pleasure: 3 }) },
      { id: 'non' },
    ],
  }),
  simpleEvent({
    id: 'pass_festival',
    kind: 'tentation',
    pleasureCategory: 'loisirs',
    stages: ['prologue', 'stage1', 'stage2'],
    season: [7, 8],
    cooldownDays: 300,
    params: (state) => ({
      price: student(state) ? between(state, 120, 170) : between(state, 150, 230),
      day: between(state, 50, 75),
    }),
    choices: [
      { id: 'pass', effects: (_, p) => ({ cash: -p.price, pleasure: 5, energy: -3 }) },
      { id: 'journee', effects: (_, p) => ({ cash: -p.day, pleasure: 2 }) },
      { id: 'non', effects: { wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    // Tout le monde part le même soir, et les billets suivent : partir à l'aube coûte moins cher.
    id: 'ponts_de_mai',
    kind: 'tentation',
    pleasureCategory: 'voyages',
    stages: ['stage1', 'stage2'],
    season: [5],
    cooldownDays: 300,
    params: (state) => {
      const early = between(state, 200, 280);
      return { early, peak: early + between(state, 60, 100) };
    },
    choices: [
      { id: 'veille', effects: (_, p) => ({ cash: -p.peak, pleasure: 4 }) },
      { id: 'aube', effects: (_, p) => ({ cash: -p.early, pleasure: 4, energy: -2 }) },
      { id: 'rester' },
    ],
  }),
  simpleEvent({
    id: 'sejour_derniere_minute',
    kind: 'tentation',
    pleasureCategory: 'voyages',
    stages: ['stage1', 'stage2'],
    params: (state) => ({ price: between(state, 450, 700) }),
    choices: [
      { id: 'partir', effects: (_, p) => ({ cash: -p.price, pleasure: 6 }) },
      {
        id: 'quatre_fois',
        effects: (_, p) => ({ loan: { amount: p.price, rate: 0.15, months: 4 }, pleasure: 6 }),
      },
      { id: 'non' },
    ],
  }),
  simpleEvent({
    id: 'semaine_au_ski',
    kind: 'tentation',
    pleasureCategory: 'voyages',
    stages: ['prologue', 'stage1', 'stage2'],
    season: [1, 2],
    cooldownDays: 300,
    // Les alternants ont déjà leur week-end au ski avec l'équipe (week_end_ski_equipe, paquet « etudes »).
    condition: (state) => state.stage !== 'prologue' || state.path !== 'alternance',
    params: (state) => ({
      price: student(state) ? between(state, 300, 420) : between(state, 500, 750),
      short: between(state, 80, 140),
    }),
    choices: [
      { id: 'semaine', effects: (_, p) => ({ cash: -p.price, pleasure: 5, energy: -2 }) },
      { id: 'luge', effects: (_, p) => ({ cash: -p.short, pleasure: 2 }) },
      { id: 'non' },
    ],
  }),
  simpleEvent({
    id: 'auberge_de_jeunesse',
    kind: 'quotidien',
    pleasureCategory: 'voyages',
    stages: ['prologue', 'stage1', 'stage2'],
    params: (state) => ({ dortoir: between(state, 40, 60), hotel: between(state, 140, 200) }),
    choices: [
      { id: 'dortoir', effects: (_, p) => ({ cash: -p.dortoir, energy: -1, wellbeing: 1 }) },
      { id: 'hotel', effects: (_, p) => ({ cash: -p.hotel, pleasure: 2 }) },
      { id: 'habitant', effects: { energy: -2 } },
    ],
  }),
];
